import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Heart, ShoppingCart, Eye, Plus, Minus } from 'lucide-react'
import { useCartStore } from '../../store/cartStore'
import { useWishlistStore } from '../../store/wishlistStore'
import { flyToCart } from '../../utils/cartFly'

function formatPrice(price) {
  if (!price && price !== 0) return 'POA'
  const num = parseFloat(price)
  if (isNaN(num)) return 'POA'
  return `R ${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
}

function getImageUrl(product) {
  const p = product?.primary_image
  // Real MappedData paths only. Skip legacy theme placeholders ("assets/…"),
  // which aren't in MappedData, so the category-emoji fallback shows instead of a 404.
  if (p && !p.startsWith('assets/')) return `/MappedData/${p}`
  if (product?.image_url) return product.image_url
  return null
}

export default function ProductCard({ product, view = 'grid' }) {
  const addItem = useCartStore(s => s.addItem)
  const cartItems = useCartStore(s => s.items)
  const { toggle, isInWishlist } = useWishlistStore()
  const [adding, setAdding] = useState(false)
  const [qty, setQty] = useState(1)
  const [imgError, setImgError] = useState(false)
  const imgRef = useRef(null)

  const wished = isInWishlist(product.id)
  const cartItem = cartItems && cartItems.find(i => i.product_id === product.id)
  const isInCart = !!cartItem
  const cartQty  = cartItem?.quantity ?? 0
  const imageUrl = getImageUrl(product)
  const name = product.description || product.product_name || product.name || 'Unknown Product'
  // inclprice = VAT-inclusive selling price (what customer pays)
  // exclprice = ex-VAT price — NOT a "was" price, just pre-tax, so no discount display
  const price = product.inclprice || product.inclprice2 || product.price
  // Only show a strikethrough/sale if there's an explicit original_price or compare_price field
  const comparePrice = product.compare_price || product.original_price || null
  const hasDiscount = comparePrice && price && parseFloat(comparePrice) > parseFloat(price)

  const handleAddToCart = useCallback(async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setAdding(true)

    // Trigger fly animation — pass the product's emoji as fallback for products without images
    flyToCart(imgRef.current, imageUrl, getCategoryEmoji(product))

    await addItem(product, qty)
    setTimeout(() => setAdding(false), 800)
  }, [product, qty, addItem, imageUrl])

  const handleWishlist = (e) => {
    e.preventDefault()
    e.stopPropagation()
    toggle(product)
  }

  const changeQty = (e, delta) => {
    e.preventDefault()
    e.stopPropagation()
    setQty(q => Math.max(1, Math.min(999, q + delta)))
  }

  const setQtyDirect = (val) => setQty(val)

  if (view === 'list') {
    return (
      <Link to={`/product/${product.id}`} className="card flex gap-4 p-4 hover:scale-[1.005] transition-transform group">
        <div ref={imgRef} className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-white">
          <ProductImage url={imageUrl} name={name} imgError={imgError} setImgError={setImgError} product={product} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary transition-colors">
            {name}
          </h3>
          {product.brand && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{product.brand}</p>
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="price text-lg">{formatPrice(price)}</span>
            <div className="flex items-center gap-2">
              <QtyControl qty={qty} onChange={changeQty} onSet={setQtyDirect} small />
              <button onClick={handleAddToCart} disabled={adding} className="btn-primary py-1.5 px-4 text-sm">
                {adding ? 'Adding…' : 'Add to Cart'}
              </button>
            </div>
          </div>
        </div>
      </Link>
    )
  }

  return (
    <Link
      to={`/product/${product.id}`}
      className="card group relative animate-fade-in"
      style={{ willChange: 'transform' }}
    >
      {/* Badges */}
      {product.is_new && <span className="badge-new">NEW</span>}
      {hasDiscount && !product.is_new && <span className="badge-sale">SALE</span>}

      {/* Image — overflow-hidden clips the slide-up panel */}
      <div ref={imgRef} className="relative overflow-hidden bg-white aspect-square">
        <ProductImage url={imageUrl} name={name} imgError={imgError} setImgError={setImgError} product={product} />

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />

        {/* Action buttons top-right */}
        {/* Heart: always visible + teal when wishlisted; hover-only when not */}
        {/* Eye: always hover-only */}
        <div className="absolute top-2 right-2 flex flex-col gap-1.5">
          <button
            onClick={handleWishlist}
            className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-md transition-all duration-200 ${
              wished
                ? 'opacity-100 translate-x-0 bg-primary text-white'
                : 'opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary hover:text-white'
            }`}
          >
            <Heart size={14} fill={wished ? 'currentColor' : 'none'} />
          </button>
          <button className="w-8 h-8 rounded-lg bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary hover:text-white flex items-center justify-center shadow-md transition-all duration-200 opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0">
            <Eye size={14} />
          </button>
        </div>

        {/* Cart icon top-left */}
        {/* In cart: always visible + teal, navigates to /cart to manage */}
        {/* Not in cart: hover-only slide-in, adds to cart */}
        <div className={`absolute top-2 left-2 transition-all duration-200 ${
          isInCart
            ? 'opacity-100 translate-x-0'
            : 'opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0'
        }`}>
          {isInCart ? (
            <Link
              to="/cart"
              onClick={e => e.stopPropagation()}
              className="relative w-8 h-8 rounded-lg flex items-center justify-center shadow-md bg-primary text-white hover:bg-primary-dark transition-colors"
              title={`In your cart (×${cartQty}) — click to manage`}
            >
              <ShoppingCart size={14} />
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-gray-900 text-[10px] font-bold font-display flex items-center justify-center leading-none shadow-sm">
                {cartQty > 99 ? '99+' : cartQty}
              </span>
            </Link>
          ) : (
            <button
              onClick={handleAddToCart}
              disabled={adding}
              className="w-8 h-8 rounded-lg flex items-center justify-center shadow-md bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary hover:text-white transition-colors disabled:opacity-50"
              title="Add to cart"
            >
              <ShoppingCart size={14} />
            </button>
          )}
        </div>

        {/* Qty + Add to Cart — slides up from bottom, stays inside image div (no height shift) */}
        <div className="absolute bottom-0 inset-x-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm flex items-center justify-center gap-3 py-2 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs font-display text-gray-500 dark:text-gray-400 uppercase tracking-wide">Qty</span>
            <QtyControl qty={qty} onChange={changeQty} onSet={setQtyDirect} />
          </div>
          <button
            onClick={handleAddToCart}
            disabled={adding}
            className="w-full bg-primary hover:bg-primary-dark text-white py-2.5 text-sm font-display font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <ShoppingCart size={15} />
            {adding ? 'Adding…' : 'Add to Cart'}
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        {product.brand && (
          <p className="text-xs text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wide mb-1">
            {product.brand}
          </p>
        )}
        <h3 className="font-display font-semibold text-gray-900 dark:text-white text-sm leading-snug line-clamp-2 group-hover:text-primary dark:group-hover:text-red-400 transition-colors">
          {name}
        </h3>

        {/* Pills row — UOM, category, promo */}
        <div className="flex flex-wrap gap-1 mt-2">
          <UomPill product={product} />
          {product.category_name && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-display font-medium bg-primary/10 text-primary dark:bg-primary/20 dark:text-teal-300">
              {getCategoryEmoji(product)} {product.category_name}
            </span>
          )}
          {product.on_promotion && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-display font-semibold bg-gold/15 text-amber-700 dark:text-gold border border-gold/30">
              🎁 Promo
            </span>
          )}
        </div>

        <div className="flex items-center justify-between mt-2">
          <div>
            <span className="price">{formatPrice(price)}</span>
            {hasDiscount && (
              <span className="text-xs text-gray-400 line-through ml-2">
                {formatPrice(comparePrice)}
              </span>
            )}
          </div>
          {product.stock_quantity === 0 && (
            <span className="text-xs text-red-500 font-medium">Out of stock</span>
          )}
        </div>
      </div>
    </Link>
  )
}

// ── Qty stepper ──────────────────────────────────────────────────────────────
function QtyControl({ qty, onChange, onSet, small = false }) {
  const sz = small ? 'w-6 h-6' : 'w-7 h-7'
  const iconSz = small ? 12 : 13
  const inputW = small ? 'w-8' : 'w-10'

  // Local draft allows the field to be empty while the user is typing
  const [draft, setDraft] = useState(String(qty))

  // Keep draft in sync when qty changes externally (e.g. +/- buttons)
  useEffect(() => { setDraft(String(qty)) }, [qty])

  const commit = () => {
    const n = parseInt(draft, 10)
    if (!isNaN(n) && n >= 1) {
      onSet && onSet(Math.max(1, Math.min(999, n)))
    } else {
      // Empty or invalid — revert to current qty
      setDraft(String(qty))
    }
  }

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={e => onChange(e, -1)}
        className={`${sz} rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary hover:text-white flex items-center justify-center transition-colors`}
      >
        <Minus size={iconSz} />
      </button>
      <input
        type="number"
        min={1}
        max={999}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          e.stopPropagation()
          if (e.key === 'Enter')  { commit(); e.target.blur() }
          if (e.key === 'Escape') { setDraft(String(qty)); e.target.blur() }
        }}
        onClick={e => { e.preventDefault(); e.stopPropagation(); e.target.select() }}
        className={`${inputW} text-center text-sm font-bold font-display text-gray-800 dark:text-white bg-transparent border border-gray-200 dark:border-gray-600 rounded-md focus:outline-none focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
      />
      <button
        onClick={e => onChange(e, 1)}
        className={`${sz} rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary hover:text-white flex items-center justify-center transition-colors`}
      >
        <Plus size={iconSz} />
      </button>
    </div>
  )
}

// ── UOM pill — derives unit of measure from packsize + description ────────────
function UomPill({ product }) {
  const ps = product?.packsize
  const desc = (product?.description || '').toLowerCase()
  const cat  = (product?.category_name || '').toLowerCase()

  let label, emoji
  if (ps === 1)                                label = 'Single',   emoji = '1️⃣'
  else if (/keg/.test(desc + cat))             label = 'Keg',      emoji = '🪣'
  else if (/can|cans/.test(desc + cat) && ps)  label = `${ps}-Pack Cans`, emoji = '🥫'
  else if (ps === 6)                           label = '6-Pack',   emoji = '📦'
  else if (ps === 12)                          label = '12-Pack',  emoji = '📦'
  else if (ps === 24)                          label = 'Case (24)', emoji = '🗃️'
  else if (ps === 48)                          label = 'Case (48)', emoji = '🗃️'
  else if (ps >= 20)                           label = `Case (${ps})`, emoji = '🗃️'
  else if (ps > 1)                             label = `Pack of ${ps}`, emoji = '📦'
  else if (/bottle/.test(desc))               label = 'Bottle',   emoji = '🍾'
  else if (!ps)                               return null
  else                                         label = `×${ps}`,   emoji = '📦'

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-display font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
      {emoji} {label}
    </span>
  )
}
function getCategoryEmoji(product) {
  const desc  = (product?.description || product?.product_name || product?.name || '').toLowerCase()
  const brand = (product?.brand || '').toLowerCase()
  const cat   = (product?.category_name || product?.category || '').toLowerCase()

  // ── Category name takes priority (most reliable signal) ──────────────
  if (/whiskey|whisky|bourbon|scotch/.test(cat))     return '🥃'
  if (/vodka/.test(cat))                              return '🍸'
  if (/\bgin\b/.test(cat))                            return '🌿'
  if (/tequila/.test(cat))                            return '🌵'
  if (/rum/.test(cat))                                return '🏝️'
  if (/brandy|cognac/.test(cat))                      return '🍂'
  if (/liqueur/.test(cat))                            return '✨'
  if (/aperitif/.test(cat))                           return '🫗'
  if (/cane/.test(cat))                               return '🌾'
  if (/shooter/.test(cat))                            return '🔫'
  if (/champagne|sparkling/.test(cat))                return '🥂'
  if (/port|sherry|muscadel/.test(cat))               return '🫙'
  if (/box wine/.test(cat))                           return '📦'
  if (/wine/.test(cat))                               return '🍷'
  if (/craft beer/.test(cat))                         return '🍻'
  if (/cider/.test(cat))                              return '🍋'
  if (/rtd cocktail/.test(cat))                       return '🍹'
  if (/rtd/.test(cat))                                return '🥤'
  if (/beer/.test(cat))                               return '🍺'
  if (/energy/.test(cat))                             return '⚡'
  if (/juice/.test(cat))                              return '🍊'
  if (/cordial/.test(cat))                            return '🍓'
  if (/water/.test(cat))                              return '🫧'
  if (/mineral/.test(cat))                            return '💧'
  if (/cigarette|tobacco/.test(cat))                  return '🚬'
  if (/spirits/.test(cat))                            return '🥃'
  if (/snack|chips|crisp|nut|biltong|droëwors|popcorn/.test(cat)) return '🍿'
  if (/glassware|glass/.test(cat))                    return '🥂'
  if (/gift|hamper|accessor/.test(cat))               return '🎁'

  // ── Description + brand fallback (for products missing category_name) ─
  const combined = `${desc} ${brand}`

  // Well-known SA snack brands checked FIRST to avoid falling through to 🍾
  if (/willards|simba|lays|pringles|doritos|niknaks|nik nak|cheesenak|cheese nak|biltong|droëwors|droewors|popcorn|pretzels|peanut|nachos|chippies/.test(combined)) return '🍿'

  // Alcohol brands
  if (/\bjw\b|johnnie walker|glenfiddich|jameson|jack daniel|highland|macallan|chivas|ballantine|old parr|bain|grant|teacher/.test(combined)) return '🥃'
  if (/\bsmirnoff\b|absolut|belvedere|grey goose|ciroc/.test(combined))  return '🍸'
  if (/\bbeefeater\b|hendrick|tanqueray|bombay|inverroche/.test(combined)) return '🌿'
  if (/\bcaptain morgan\b|bacardi|havana|malibu/.test(combined))         return '🏝️'
  if (/\bklipdrift\b|richelieu|old brown|van ryn|hennessy|remy|brandy/.test(combined)) return '🍂'
  if (/\bamarula\b|baileys|kahlua|disaronno|frangelico/.test(combined))  return '✨'
  if (/\baperol\b|campari|vermouth|pimms/.test(combined))                return '🫗'
  if (/\bcastle\b|\bbud\b|\bheineken\b|\bcarlsberg\b|\bstella\b|\bwindhoek\b/.test(combined)) return '🍺'

  // Description keyword fallback
  if (/whiskey|whisky|bourbon|scotch/.test(combined))  return '🥃'
  if (/vodka/.test(combined))                           return '🍸'
  if (/\bgin\b/.test(combined))                         return '🌿'
  if (/tequila|mezcal/.test(combined))                  return '🌵'
  if (/\brum\b/.test(combined))                         return '🏝️'
  if (/brandy|cognac/.test(combined))                   return '🍂'
  if (/liqueur|amarula|baileys|kahlua/.test(combined))  return '✨'
  if (/aperitif|aperol|campari|vermouth/.test(combined)) return '🫗'
  if (/\bcane\b/.test(combined))                        return '🌾'
  if (/shooter/.test(combined))                         return '🔫'
  if (/champagne|sparkling|prosecco|mcc/.test(combined)) return '🥂'
  if (/port|sherry|muscadel/.test(combined))            return '🫙'
  if (/box wine/.test(combined))                        return '📦'
  if (/red wine|white wine|rosé|rose wine/.test(combined)) return '🍷'
  if (/wine/.test(combined))                            return '🍷'
  if (/craft beer|craft/.test(combined))                return '🍻'
  if (/cider/.test(combined))                           return '🍋'
  if (/rtd cocktail/.test(combined))                    return '🍹'
  if (/rtd/.test(combined))                             return '🥤'
  if (/beer|lager|ale|stout/.test(combined))            return '🍺'
  if (/energy drink|monster|red bull/.test(combined))   return '⚡'
  if (/juice|nectar|fruit/.test(combined))              return '🍊'
  if (/cordial|squash|syrup/.test(combined))            return '🍓'
  if (/mineral water|water/.test(combined))             return '🫧'
  if (/mineral|soda|soft drink|fizzy/.test(combined))   return '💧'
  if (/cigarette|tobacco|smoke/.test(combined))         return '🚬'
  if (/glass|glassware/.test(combined))                 return '🥂'
  if (/gift|hamper/.test(combined))                     return '🎁'
  if (/snack|nut|chip|crisp|popcorn|biltong/.test(combined)) return '🍿'

  return '🍾'
}

// ── Product image ─────────────────────────────────────────────────────────────
function ProductImage({ url, name, imgError, setImgError, product }) {
  if (!url || imgError) {
    const emoji = getCategoryEmoji(product)
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/5 to-gold/10 dark:from-primary/10 dark:to-gold/5">
        <div className="text-center p-4">
          <div className="text-4xl mb-2">{emoji}</div>
          <p className="text-xs text-gray-400 font-display line-clamp-2 text-center">{name}</p>
        </div>
      </div>
    )
  }

  return (
    <img
      src={url}
      alt={name}
      onError={() => setImgError(true)}
      className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105"
    />
  )
}
